// Member-facing hooks for the RaisedBeef Network desk (/network).
//
// Phase 2 of BLOOMBERG_CHAT_BUILD_PLAN.md — direct messages on the chat_threads/
// participants/messages spine. Reads are RLS-governed (thread participants);
// writes go through the SECURITY DEFINER member RPCs. Live delivery via Supabase
// Realtime on chat_messages, subscribed per active thread.

import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { DataCardMeta } from "@/components/network/DataCard";
import type { PackerChartSeriesPayload } from "@/hooks/usePackerChartSeries";

function rpc(fn: string, args: Record<string, unknown> = {}) {
  return (
    supabase as unknown as {
      rpc: (f: string, a: Record<string, unknown>) => Promise<{ data: unknown; error: unknown }>;
    }
  ).rpc(fn, args);
}
function asError(e: unknown): Error {
  return e instanceof Error ? e : new Error(String(e));
}

// ── Am I a member? (resolves current_chat_member_id) ────────────────────────
export function useMyChatMembership() {
  return useQuery<{ memberId: string | null }>({
    queryKey: ["chat-my-membership"],
    queryFn: async () => {
      const { data, error } = await rpc("current_chat_member_id");
      if (error) throw asError(error);
      // scalar uuid or null
      const id = Array.isArray(data) ? (data[0] ?? null) : (data ?? null);
      return { memberId: id ? String(id) : null };
    },
    staleTime: 60_000,
  });
}

// ── Directory (approved members you can DM) ─────────────────────────────────
export interface DirectoryMember {
  id: string;
  displayName: string;
  operationName: string | null;
  role: string;
  region: string | null;
  state: string | null;
  avatarUrl: string | null;
}
export function useChatDirectory() {
  return useQuery<DirectoryMember[]>({
    queryKey: ["chat-directory"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("v_chat_directory")
        .select("id, display_name, operation_name, role, region, state, avatar_url")
        .order("display_name", { ascending: true });
      if (error) throw asError(error);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data ?? []).map((r: any) => ({
        id: String(r.id),
        displayName: String(r.display_name ?? ""),
        operationName: r.operation_name ?? null,
        role: String(r.role ?? ""),
        region: r.region ?? null,
        state: r.state ?? null,
        avatarUrl: r.avatar_url ?? null,
      }));
    },
    staleTime: 60_000,
  });
}

// ── Thread inbox ────────────────────────────────────────────────────────────
export interface ChatThread {
  threadId: string;
  otherMemberId: string;
  otherName: string;
  otherRole: string;
  lastMessageAt: string | null;
  lastPreview: string | null;
  unread: number;
  otherLastReadAt: string | null;
  otherAvatarUrl: string | null;
  muted: boolean;
}
export function useChatThreads() {
  return useQuery<ChatThread[]>({
    queryKey: ["chat-threads"],
    queryFn: async () => {
      const { data, error } = await rpc("chat_list_threads");
      if (error) throw asError(error);
      if (!Array.isArray(data)) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return data.map((r: any) => ({
        threadId: String(r.thread_id),
        otherMemberId: String(r.other_member_id),
        otherName: String(r.other_name ?? ""),
        otherRole: String(r.other_role ?? ""),
        lastMessageAt: r.last_message_at ?? null,
        lastPreview: r.last_preview ?? null,
        unread: Number(r.unread ?? 0),
        otherLastReadAt: r.other_last_read_at ?? null,
        otherAvatarUrl: r.other_avatar_url ?? null,
        muted: Boolean(r.muted),
      }));
    },
    refetchInterval: 60_000,
  });
}

// ── Messages in a thread (+ realtime) ───────────────────────────────────────
export interface ChatMessage {
  id: string;
  senderId: string;
  senderName: string;
  kind: string;
  body: string | null;
  deleted: boolean;
  metadata: Record<string, unknown>;
  createdAt: string;
}
export function useChatMessages(threadId: string | null) {
  const qc = useQueryClient();

  const query = useQuery<ChatMessage[]>({
    queryKey: ["chat-messages", threadId],
    enabled: !!threadId,
    queryFn: async () => {
      const { data, error } = await rpc("chat_list_messages", {
        p_thread_id: threadId,
        p_limit: 100,
        p_before: null,
      });
      if (error) throw asError(error);
      if (!Array.isArray(data)) return [];
      // RPC returns newest-first; reverse to chronological for display.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return data
        .map((r: any) => ({
          id: String(r.id),
          senderId: String(r.sender_id ?? ""),
          senderName: String(r.sender_name ?? ""),
          kind: String(r.kind ?? "text"),
          body: r.body ?? null,
          deleted: Boolean(r.deleted),
          metadata: (r.metadata ?? {}) as Record<string, unknown>,
          createdAt: String(r.created_at),
        }))
        .reverse();
    },
  });

  // Live delivery: any change to this thread's messages refetches.
  useEffect(() => {
    if (!threadId) return;
    const channel = supabase
      .channel(`chat:${threadId}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "chat_messages", filter: `thread_id=eq.${threadId}` },
        () => {
          qc.invalidateQueries({ queryKey: ["chat-messages", threadId] });
          qc.invalidateQueries({ queryKey: ["chat-threads"] });
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [threadId, qc]);

  return query;
}

// ── Mutations ───────────────────────────────────────────────────────────────
export function useOpenDirect() {
  const qc = useQueryClient();
  return useMutation<string, Error, string>({
    mutationFn: async (otherMemberId) => {
      const { data, error } = await rpc("chat_open_direct", { p_other_member_id: otherMemberId });
      if (error) throw asError(error);
      const id = Array.isArray(data) ? data[0] : data;
      return String(id);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat-threads"] }),
  });
}

export function useSendMessage() {
  const qc = useQueryClient();
  return useMutation<void, Error, { threadId: string; body: string; mentionIds?: string[] }>({
    mutationFn: async ({ threadId, body, mentionIds }) => {
      const { error } = await rpc("chat_send_message", {
        p_thread_id: threadId,
        p_body: body,
        p_kind: "text",
        p_metadata: {},
        p_mention_member_ids: mentionIds ?? [],
      });
      if (error) throw asError(error);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["chat-messages", vars.threadId] });
      qc.invalidateQueries({ queryKey: ["chat-threads"] });
    },
  });
}

// Remove a DM from the caller's inbox (soft hide; reappears on a new message).
export function useHideThread() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (threadId) => {
      const { error } = await rpc("chat_hide_thread", { p_thread_id: threadId });
      if (error) throw asError(error);
    },
    onMutate: async (threadId) => {
      // Optimistic: drop the row immediately so the swipe feels instant.
      await qc.cancelQueries({ queryKey: ["chat-threads"] });
      const prev = qc.getQueryData<ChatThread[]>(["chat-threads"]);
      qc.setQueryData<ChatThread[]>(["chat-threads"], (old) =>
        (old ?? []).filter((t) => t.threadId !== threadId),
      );
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      const c = ctx as { prev?: ChatThread[] } | undefined;
      if (c?.prev) qc.setQueryData(["chat-threads"], c.prev);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: ["chat-threads"] }),
  });
}

// Mute / unmute a conversation (also silences chat push for the caller).
export function useMuteThread() {
  const qc = useQueryClient();
  return useMutation<void, Error, { threadId: string; muted: boolean }>({
    mutationFn: async ({ threadId, muted }) => {
      const { error } = await rpc("chat_set_thread_muted", { p_thread_id: threadId, p_muted: muted });
      if (error) throw asError(error);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat-threads"] }),
  });
}

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: async (threadId) => {
      const { error } = await rpc("chat_mark_read", { p_thread_id: threadId });
      if (error) throw asError(error);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["chat-threads"] });
      qc.invalidateQueries({ queryKey: ["network-rooms"] });
    },
  });
}

// ── Phase 6: message search (own threads) ──────────────────────────────────
export interface SearchResult {
  messageId: string;
  threadId: string;
  otherName: string;
  senderName: string;
  snippet: string;
  kind: string;
  createdAt: string;
}
export function useChatSearch(query: string) {
  const q = query.trim();
  return useQuery<SearchResult[]>({
    queryKey: ["chat-search", q],
    enabled: q.length >= 2,
    staleTime: 15_000,
    queryFn: async () => {
      const { data, error } = await rpc("chat_search_messages", { p_query: q, p_limit: 30 });
      if (error) throw asError(error);
      if (!Array.isArray(data)) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return data.map((r: any) => ({
        messageId: String(r.message_id),
        threadId: String(r.thread_id),
        otherName: String(r.other_name ?? ""),
        senderName: String(r.sender_name ?? ""),
        snippet: String(r.snippet ?? ""),
        kind: String(r.kind ?? "text"),
        createdAt: String(r.created_at),
      }));
    },
  });
}

// ── Phase 6: identity card ──────────────────────────────────────────────────
export interface MemberCard {
  id: string;
  displayName: string;
  operationName: string | null;
  role: string;
  region: string | null;
  state: string | null;
  bio: string | null;
  memberSince: string | null;
}
export function useMemberCard(memberId: string | null) {
  return useQuery<MemberCard | null>({
    queryKey: ["member-card", memberId],
    enabled: !!memberId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await rpc("chat_member_card", { p_member_id: memberId });
      if (error) throw asError(error);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const r = (Array.isArray(data) ? data[0] : data) as any;
      if (!r) return null;
      return {
        id: String(r.id),
        displayName: String(r.display_name ?? ""),
        operationName: r.operation_name ?? null,
        role: String(r.role ?? ""),
        region: r.region ?? null,
        state: r.state ?? null,
        bio: r.bio ?? null,
        memberSince: r.member_since ?? null,
      };
    },
  });
}

// ── Phase 5b: total unread for the nav badge (in-app notification) ──────────
export function useNetworkUnread(enabled = true) {
  const qc = useQueryClient();
  const q = useQuery<number>({
    queryKey: ["network-unread"],
    enabled,
    retry: false,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const { data, error } = await rpc("chat_total_unread");
      if (error) return 0; // non-member / error → no badge
      const v = Array.isArray(data) ? data[0] : data;
      return Number(v ?? 0);
    },
  });

  useEffect(() => {
    if (!enabled) return;
    const ch = supabase
      .channel("network-unread-watch")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "chat_messages" }, () => {
        qc.invalidateQueries({ queryKey: ["network-unread"] });
      })
      .subscribe();
    return () => {
      void supabase.removeChannel(ch);
    };
  }, [qc, enabled]);

  return q.data ?? 0;
}

// ── Phase 5a: presence (who's online) — ephemeral, Supabase Realtime ────────
export function useNetworkPresence(myMemberId: string | null, myName: string) {
  const [online, setOnline] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!myMemberId) return;
    const ch = supabase.channel("network-presence", { config: { presence: { key: myMemberId } } });
    ch.on("presence", { event: "sync" }, () => {
      setOnline(new Set(Object.keys(ch.presenceState())));
    }).subscribe((status) => {
      if (status === "SUBSCRIBED") void ch.track({ name: myName });
    });
    return () => {
      void supabase.removeChannel(ch);
    };
  }, [myMemberId, myName]);
  return online;
}

// ── Phase 5a: typing indicator + read broadcast on the active thread ────────
export function useThreadTyping(threadId: string | null, myMemberId: string | null, myName: string) {
  const qc = useQueryClient();
  const [peerTyping, setPeerTyping] = useState(false);
  const [peerTypingName, setPeerTypingName] = useState("");
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const clearRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSentRef = useRef(0);

  useEffect(() => {
    if (!threadId || !myMemberId) return;
    const ch = supabase.channel(`thread-live:${threadId}`);
    ch.on("broadcast", { event: "typing" }, ({ payload }) => {
      if (!payload || payload.memberId === myMemberId) return;
      setPeerTyping(true);
      setPeerTypingName(payload.name || "");
      if (clearRef.current) clearTimeout(clearRef.current);
      clearRef.current = setTimeout(() => setPeerTyping(false), 3000);
    })
      .on("broadcast", { event: "read" }, ({ payload }) => {
        if (!payload || payload.memberId === myMemberId) return;
        // Peer read the thread → refresh so the "Seen" marker updates promptly.
        qc.invalidateQueries({ queryKey: ["chat-threads"] });
      })
      .subscribe();
    channelRef.current = ch;
    return () => {
      if (clearRef.current) clearTimeout(clearRef.current);
      void supabase.removeChannel(ch);
      channelRef.current = null;
      setPeerTyping(false);
    };
  }, [threadId, myMemberId, qc]);

  const notifyTyping = useCallback(() => {
    const now = Date.now();
    if (now - lastSentRef.current < 1500) return; // throttle broadcasts
    lastSentRef.current = now;
    void channelRef.current?.send({ type: "broadcast", event: "typing", payload: { memberId: myMemberId, name: myName } });
  }, [myMemberId, myName]);

  const notifyRead = useCallback(() => {
    void channelRef.current?.send({ type: "broadcast", event: "read", payload: { memberId: myMemberId } });
  }, [myMemberId]);

  return { peerTyping, peerTypingName, notifyTyping, notifyRead };
}

// ── Live "as-of-now" read for a data card (Phase 4c) ────────────────────────
export interface CardLive {
  value: number | null;
  verdict: string | null;
  unit: string | null;
  asOf: string | null;
}
export function useCardLive(cardType: string | undefined, params: Record<string, unknown> | undefined) {
  return useQuery<CardLive>({
    queryKey: ["card-live", cardType, params],
    enabled: !!cardType,
    staleTime: 5 * 60 * 1000,
    retry: false,
    queryFn: async () => {
      const { data, error } = await rpc("chat_card_live", {
        p_card_type: cardType,
        p_params: params ?? {},
      });
      if (error) throw asError(error);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const r = (Array.isArray(data) ? data[0] : data) as any;
      return {
        value: r?.value != null ? Number(r.value) : null,
        verdict: r?.verdict ?? null,
        unit: r?.unit ?? null,
        asOf: r?.as_of ?? null,
      };
    },
  });
}

// ── Pinned members (roster favorites) ──────────────────────────────────────
export function usePinnedMembers() {
  return useQuery<Set<string>>({
    queryKey: ["chat-pins"],
    staleTime: 60_000,
    queryFn: async () => {
      const { data, error } = await rpc("chat_list_pins");
      if (error) return new Set<string>();
      const arr = Array.isArray(data) ? data : [];
      // setof uuid → may arrive as scalars or single-key objects.
      const ids = arr
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .map((r: any) => (typeof r === "string" ? r : r?.chat_list_pins ?? Object.values(r ?? {})[0]))
        .filter(Boolean)
        .map(String);
      return new Set<string>(ids);
    },
  });
}
export function usePinMember() {
  const qc = useQueryClient();
  return useMutation<void, Error, { memberId: string; pin: boolean }>({
    mutationFn: async ({ memberId, pin }) => {
      const { error } = await rpc(pin ? "chat_pin_member" : "chat_unpin_member", { p_member_id: memberId });
      if (error) throw asError(error);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["chat-pins"] }),
  });
}

// ── Barn directory (for the barn_report card picker) ────────────────────────
export interface DirectoryBarn {
  slugId: number;
  barnName: string;
  city: string | null;
  stateCode: string | null;
  region: string | null;
  lastReportDate: string | null;
}
export function useBarnDirectory() {
  return useQuery<DirectoryBarn[]>({
    queryKey: ["network-barn-directory"],
    queryFn: async () => {
      const { data, error } = await rpc("rpc_barn_directory");
      if (error) throw asError(error);
      if (!Array.isArray(data)) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return data.map((b: any) => ({
        slugId: Number(b.slug_id),
        barnName: String(b.barn_name ?? ""),
        city: b.city ?? null,
        stateCode: b.state_code ?? null,
        region: b.region ?? null,
        lastReportDate: b.last_report_date ?? null,
      }));
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ── Send a data card (Phase 4a — server resolves the snapshot) ──────────────
export function useSendDataCard() {
  const qc = useQueryClient();
  return useMutation<void, Error, { threadId: string; cardType: string; params: Record<string, unknown>; counterTo?: string | null }>({
    mutationFn: async ({ threadId, cardType, params, counterTo }) => {
      const { error } = await rpc("chat_send_data_card", {
        p_thread_id: threadId,
        p_card_type: cardType,
        p_params: params,
        p_counter_to: counterTo ?? null,
      });
      if (error) throw asError(error);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["chat-messages", vars.threadId] });
      qc.invalidateQueries({ queryKey: ["chat-threads"] });
    },
  });
}

// ── Pinned data cards (D5 — live cards in a thread/room header) ──────────────
export interface PinnedCard {
  id: string;
  cardType: string;
  params: Record<string, unknown>;
  label: string | null;
  pinnedByName: string | null;
  // The full card meta, RE-RESOLVED LIVE server-side (null if its source has no
  // data right now — the header renders a muted "unavailable" chip).
  meta: DataCardMeta | null;
}
export function usePinnedCards(threadId: string | null) {
  return useQuery<PinnedCard[]>({
    queryKey: ["chat-pinned-cards", threadId],
    enabled: !!threadId,
    staleTime: 60_000,
    refetchInterval: 5 * 60 * 1000, // keep the header live without a reload
    queryFn: async () => {
      const { data, error } = await rpc("chat_list_pinned_cards", { p_thread_id: threadId });
      if (error) throw asError(error);
      if (!Array.isArray(data)) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return data.map((r: any) => ({
        id: String(r.id),
        cardType: String(r.card_type),
        params: r.params ?? {},
        label: r.label ?? null,
        pinnedByName: r.pinned_by_name ?? null,
        meta: (r.meta ?? null) as DataCardMeta | null,
      }));
    },
  });
}
export function usePinCard() {
  const qc = useQueryClient();
  return useMutation<void, Error, { threadId: string; cardType: string; params: Record<string, unknown> }>({
    mutationFn: async ({ threadId, cardType, params }) => {
      const { error } = await rpc("chat_pin_card", { p_thread_id: threadId, p_card_type: cardType, p_params: params });
      if (error) throw asError(error);
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ["chat-pinned-cards", vars.threadId] }),
  });
}
export function useUnpinCard() {
  const qc = useQueryClient();
  return useMutation<void, Error, { threadId: string; pinId: string }>({
    mutationFn: async ({ pinId }) => {
      const { error } = await rpc("chat_unpin_card", { p_pin_id: pinId });
      if (error) throw asError(error);
    },
    onSuccess: (_d, vars) => qc.invalidateQueries({ queryKey: ["chat-pinned-cards", vars.threadId] }),
  });
}

// ── Charts in chat (D4) ─────────────────────────────────────────────────────
// Re-resolves a chart series LIVE through the SAME server-resolved RPCs the
// consultant uses (feeder_* → get_feeder_chart_series, else get_packer_chart_series).
// No LLM — pure SQL payloads, rendered by the consultant's ChartDispatcher.
export function useNetworkChartSeries(
  templateId: string | undefined,
  slots: Record<string, unknown> | undefined,
  enabled = true,
) {
  return useQuery<PackerChartSeriesPayload>({
    queryKey: ["network-chart", templateId, slots ? JSON.stringify(slots) : null],
    enabled: enabled && !!templateId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
    queryFn: async () => {
      const fn = templateId!.startsWith("feeder_") ? "get_feeder_chart_series" : "get_packer_chart_series";
      const { data, error } = await rpc(fn, { p_template_id: templateId, p_slots: slots ?? {} });
      if (error) throw asError(error);
      return (Array.isArray(data) ? data[0] : data) as PackerChartSeriesPayload;
    },
  });
}
export function useSendChart() {
  const qc = useQueryClient();
  return useMutation<void, Error, { threadId: string; templateId: string; slots: Record<string, unknown> }>({
    mutationFn: async ({ threadId, templateId, slots }) => {
      const { error } = await rpc("chat_send_chart", { p_thread_id: threadId, p_template_id: templateId, p_slots: slots });
      if (error) throw asError(error);
    },
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({ queryKey: ["chat-messages", vars.threadId] });
      qc.invalidateQueries({ queryKey: ["chat-threads"] });
    },
  });
}
